import {
  LayoutDashboard,
  LayoutTemplate,
  LayoutList,
  Boxes,
  ListChecks,
  Inbox,
  Calculator,
  Library,
  MessagesSquare,
  MessageSquare,
  Target,
  Waypoints,
  Newspaper,
  CalendarDays,
  Ban,
  FileText,
  Scale,
  GitCommitVertical,
  Server,
  Sparkles,
  Compass,
  UsersRound,
  Share2,
  Rocket,
  CalendarRange,
  ClipboardList,
  HeartPulse,
  Settings,
  Info,
  Plug,
  Database,
  FileStack,
  RotateCcw,
  Timer,
  type LucideIcon,
} from 'lucide-react';

/**
 * Single source of truth for the sidebar navigation. Organized into ordered,
 * collapsible groups (Overview, Goals, Planning, Team, Knowledge, Intelligence).
 *
 * IMPORTANT: this list drives the Sidebar ONLY — the router (<Routes> in
 * App.tsx) is defined independently, so restructuring the menu never affects
 * routing. EVERY pre-existing route is preserved here (Dashboard, Templates,
 * Rooms, Tasks, Estimation, code-Knowledge, Chat, plus all batch 1–5 routes);
 * the additive Batch 6 entries are My Vector, Team Vector, the goal
 * framework/health quick-views, and the Entity Graph.
 *
 * Some items carry a query string (e.g. `/goals?framework=BHAG`): they deep-link
 * into an existing page's client-side filter, adding a view WITHOUT adding a
 * route or a page.
 */
export interface NavItem {
  /** Route path (react-router), optionally with a `?query` for a filtered view. */
  path: string;
  /** Sidebar label. */
  label: string;
  icon: LucideIcon;
}

/** A collapsible group of nav items sharing a heading. */
export interface NavSection {
  /** Group heading. */
  heading: string;
  items: NavItem[];
  /** Whether the group starts expanded (default true). */
  defaultOpen?: boolean;
}

/**
 * The ordered, grouped navigation. Preserves every existing route (Home,
 * Goals, all Planning/Team/Knowledge/Intelligence pages) — folded into the
 * Batch-7 primary structure (Home / Goals / Work / Team / Knowledge / Insights /
 * Ask Vector) — and adds the Sprint Board's Current Sprint + Daily Scrum. No
 * route/page was removed; only the MENU was reorganized (see App.tsx for the
 * independent router).
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    heading: 'Home',
    items: [{ path: '/', label: 'Home', icon: LayoutDashboard }],
  },
  {
    heading: 'Goals',
    items: [
      { path: '/goals', label: 'All Goals', icon: Target },
      { path: '/goals?framework=BHAG', label: 'BHAG', icon: Rocket },
      { path: '/goals?framework=Quarterly', label: 'Quarterly', icon: CalendarRange },
      { path: '/goals?framework=OP1', label: 'OP1', icon: ClipboardList },
      { path: '/goals?framework=OP2', label: 'OP2', icon: ClipboardList },
      { path: '/goals?view=health', label: 'Goal Health', icon: HeartPulse },
    ],
  },
  {
    heading: 'Work',
    items: [
      { path: '/work/sprint', label: 'Current Sprint', icon: LayoutList },
      { path: '/work/backlog', label: 'Backlog', icon: Inbox },
      { path: '/tasks', label: 'All Tasks', icon: ListChecks },
      { path: '/work/sprints', label: 'Sprints', icon: CalendarRange },
      { path: '/retro', label: 'Sprint Retro', icon: RotateCcw },
      { path: '/estimation', label: 'Estimation', icon: Calculator },
      { path: '/templates', label: 'Templates', icon: LayoutTemplate },
      { path: '/task-templates', label: 'Task Templates', icon: FileStack },
      { path: '/rooms', label: 'Rooms', icon: Boxes },
    ],
  },
  {
    heading: 'Team',
    defaultOpen: false,
    items: [
      { path: '/team/daily-scrum', label: 'Daily Scrum', icon: MessageSquare },
      { path: '/standup', label: 'Standup', icon: Timer },
      { path: '/daily-updates', label: 'Daily Updates', icon: CalendarDays },
      { path: '/workstreams', label: 'Workstreams', icon: Waypoints },
      { path: '/blockers', label: 'Blockers', icon: Ban },
      { path: '/changes', label: 'Change Feed', icon: Newspaper },
      { path: '/teams', label: 'Teams', icon: UsersRound },
      { path: '/onboarding', label: 'Team Onboarding', icon: Plug },
      { path: '/sharing', label: 'Tenant Sharing', icon: Share2 },
    ],
  },
  {
    heading: 'Knowledge',
    defaultOpen: false,
    items: [
      { path: '/documents', label: 'Documents', icon: FileText },
      { path: '/knowledge', label: 'Code Knowledge', icon: Library },
      { path: '/knowledge-bases', label: 'Knowledge Bases', icon: Database },
      { path: '/chat', label: 'Chat', icon: MessagesSquare },
    ],
  },
  {
    heading: 'Insights',
    defaultOpen: false,
    items: [
      { path: '/recap', label: 'Recap', icon: Sparkles },
      { path: '/graph', label: 'Graph', icon: Share2 },
      { path: '/decisions', label: 'Decisions', icon: Scale },
      { path: '/timeline', label: 'Engineering Timeline', icon: GitCommitVertical },
      { path: '/services', label: 'Services', icon: Server },
      { path: '/experts', label: 'Experts', icon: UsersRound },
    ],
  },
  {
    heading: 'Ask Vector',
    items: [{ path: '/ask', label: 'Ask Vector', icon: Compass }],
  },
  {
    heading: 'System',
    defaultOpen: false,
    items: [
      { path: '/about', label: 'About Vector', icon: Info },
      { path: '/settings', label: 'Settings', icon: Settings },
    ],
  },
];

/** Flat list of all nav items (order-preserving), for any consumer that needs it. */
export const NAV_ITEMS: NavItem[] = NAV_SECTIONS.flatMap((s) => s.items);

/** Returns the ordered, grouped navigation sections. */
export function navSections(): NavSection[] {
  return NAV_SECTIONS;
}

/**
 * Determines whether a nav item is the active one for the current location.
 *
 * - Query-string items (e.g. `/goals?framework=BHAG`) are active only on an
 *   EXACT pathname + matching query match.
 * - Plain items are active when the pathname matches (or is a child route, e.g.
 *   `/goals/123` activates `/goals`) AND there is no query string — so a
 *   filtered quick-view never lights up the plain item too.
 * - `/` (Dashboard) matches only its exact pathname.
 */
export function isNavItemActive(
  itemPath: string,
  pathname: string,
  search: string,
): boolean {
  const [p, q = ''] = itemPath.split('?');
  const currentQuery = normalizeQuery(search);

  if (q) {
    return pathname === p && currentQuery === normalizeQuery(q);
  }
  if (currentQuery !== '') {
    // A filtered view is showing; a plain item for the same base path defers.
    if (pathname === p || pathname.startsWith(`${p}/`)) return false;
  }
  if (p === '/') return pathname === '/';
  return pathname === p || pathname.startsWith(`${p}/`);
}

/** Normalizes a query string (drops leading `?`, sorts params) for comparison. */
function normalizeQuery(search: string): string {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  if (!raw) return '';
  const params = new URLSearchParams(raw);
  const entries = [...params.entries()].sort(([a], [b]) => a.localeCompare(b));
  return entries.map(([k, v]) => `${k}=${v}`).join('&');
}
